import { FeedModal } from "./FeedModal";
import { useEventStore } from "store";
import bingoBlackIcon from "../../assets/images/bingo-black-icon.png";
import bingoRedIcon from "../../assets/images/bingo-red-icon.png";
import photoChallengeBlackIcon from "../../assets/images/photo-challenge-black-icon.png";
import photoChallengeRedIcon from "../../assets/images/photo-challenge-red-icon.png";
import quizBlackIcon from "../../assets/images/quiz-black-icon.png";
import quizRedIcon from "../../assets/images/quiz-red-icon.png";
import teamMatchBlackIcon from "../../assets/images/team-match-black-icon.png";
import teamMatchRedIcon from "../../assets/images/team-match-red-icon.png";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

const BLACK_ICONS = {
  bingo: bingoBlackIcon,
  photo_challenge: photoChallengeBlackIcon,
  quiz: quizBlackIcon,
  team_match: teamMatchBlackIcon,
};

const RED_ICONS = {
  bingo: bingoRedIcon,
  photo_challenge: photoChallengeRedIcon,
  quiz: quizRedIcon,
  team_match: teamMatchRedIcon,
};

interface OptionCardMenuProps {
  icon: string;
  title: string;
  description: string;
  onClick: () => void;
}
export const OptionCardMenu = ({
  icon,
  title,
  description,
  onClick,
}: OptionCardMenuProps) => {
  const { theme } = useEventStore();

  return (
    <button
      type="button"
      className={`w-full flex items-center px-5 py-3 gap-2 ${
        theme.name === "rose" ? `card ${theme.card}` : `button ${theme.button}`
      } transition-transform duration-200 shadow-md hover:shadow-lg`}
      onClick={onClick}
    >
      <img
        src={icon}
        alt={title}
        className="w-16 h-16 transition-transform max-h-16 object-contain"
      />
      <div className="flex flex-col items-center grow">
        <h3 className="text-lg font-semibold text-theme-black text-center">
          {title}
        </h3>
        <p className="text-theme-black text-sm w-48">{description}</p>
      </div>
    </button>
  );
};

type GamesModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export default function GamesModal({ isOpen, onClose }: GamesModalProps) {
  const { theme, event } = useEventStore();
  const navigate = useNavigate();
  const intl = useIntl();

  const menuIcons = useMemo(
    () => (theme.name === "rose" ? RED_ICONS : BLACK_ICONS),
    [theme]
  );

  const bingoGameEnabled = useMemo(
    () => event?.betaFeatures?.includes("bingo"),
    [event]
  );

  const photoChallengeGameEnabled = useMemo(
    () => event?.betaFeatures?.includes("photo_challenge"),
    [event]
  );

  const quizGameEnabled = useMemo(
    () => event?.betaFeatures?.includes("quiz"),
    [event]
  );

  const teamMatchEnabled = useMemo(
    () => event?.betaFeatures?.includes("team_match"),
    [event]
  );

  const handleBingoClick = () => {
    navigate("../../games/bingo");
  };

  const handlePhotoChallengeClick = () => {
    navigate("../../games/photo-challenge");
  };

  const handleQuizClick = () => {
    navigate("../../games/quiz");
  };

  const handleTeamMatchClick = () => {
    navigate("../../games/team-match");
  };

  return (
    <FeedModal onClose={onClose} isOpen={isOpen}>
      <div className="py-6 flex flex-col items-center h-full w-full">
        <h3 className="text-lg font-century-gothic-bold text-center">
          <FormattedMessage
            id="feed.modalGames.title"
            defaultMessage="Games da Festa"
          />
        </h3>
        <div className="flex flex-col items-center gap-4 grow flex-1 w-full max-h-[60vh] px-6 mt-3 pt-3 pb-3 overflow-y-auto">
          {bingoGameEnabled && (
            <OptionCardMenu
              icon={menuIcons.bingo}
              title={intl.formatMessage({
                id: "feed.menu.button.options.bingoGame",
                defaultMessage: "Bingo",
              })}
              description={intl.formatMessage({
                id: "feed.menu.message.options.bingoGame",
                defaultMessage: "Jogue o bingo da festa!",
              })}
              onClick={handleBingoClick}
            />
          )}
          {photoChallengeGameEnabled && (
            <OptionCardMenu
              icon={menuIcons.photo_challenge}
              title={intl.formatMessage({
                id: "feed.menu.button.options.photoChallengeGame",
                defaultMessage: "Desafio de fotos",
              })}
              description={intl.formatMessage({
                id: "feed.menu.message.options.photoChallengeGame",
                defaultMessage: "Participe do desafio de fotos!",
              })}
              onClick={handlePhotoChallengeClick}
            />
          )}
          {quizGameEnabled && (
            <OptionCardMenu
              icon={menuIcons.quiz}
              title={intl.formatMessage({
                id: "feed.menu.button.options.quizGame",
                defaultMessage: "Quiz",
              })}
              description={intl.formatMessage({
                id: "feed.menu.message.options.quizGame",
                defaultMessage: "Teste seus conhecimentos!",
              })}
              onClick={handleQuizClick}
            />
          )}
          {teamMatchEnabled && (
            <OptionCardMenu
              icon={menuIcons.team_match}
              title={intl.formatMessage({
                id: "feed.menu.button.options.teamMatchGame",
                defaultMessage: "Batalha dos Noivos",
              })}
              description={intl.formatMessage({
                id: "feed.menu.message.options.teamMatchGame",
                defaultMessage: "De qual time você faz parte?",
              })}
              onClick={handleTeamMatchClick}
            />
          )}
        </div>
      </div>
    </FeedModal>
  );
}
